import { useState, useEffect, useCallback, useMemo } from "react";
import { useAdmin } from "../../../contexts/AdminContext";

const DEFAULT_PHOTOS = [
  {
    src: "https://readdy.ai/api/search-image?query=stunning%20modern%20luxury%20white%20villa%20exterior%20infinity%20pool&width=1200&height=800&seq=gallery-1",
    label: "Façade & Piscine",
    category: "exterieur",
  },
  {
    src: "https://readdy.ai/api/search-image?query=luxury%20villa%20interior%20living%20room%20modern&width=1200&height=800&seq=gallery-4",
    label: "Salon panoramique",
    category: "interieur",
  },
];

const categories = [
  { key: "all", label: "Toutes" },
  { key: "exterieur", label: "Extérieur" },
  { key: "interieur", label: "Intérieur" },
];

export default function PropertyGallery() {
  const { settings } = useAdmin();
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const photos = useMemo(() => {
    if (settings.propertyImages.gallery && settings.propertyImages.gallery.length > 0) {
      return settings.propertyImages.gallery.map((url, i) => ({
        src: url,
        label: i === 0 ? "Photo principale" : `Vue ${i + 1}`,
        category: i % 2 === 0 ? "exterieur" : "interieur" // Mocking category for dynamic images
      }));
    }
    return DEFAULT_PHOTOS;
  }, [settings.propertyImages.gallery]);

  const filtered = cat === "all" ? photos : photos.filter((p) => p.category === cat);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prevPhoto = useCallback(() => {
    setLightbox((i) => (i !== null ? (i - 1 + filtered.length) % filtered.length : null));
  }, [filtered.length]);
  const nextPhoto = useCallback(() => {
    setLightbox((i) => (i !== null ? (i + 1) % filtered.length : null));
  }, [filtered.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevPhoto();
      if (e.key === "ArrowRight") nextPhoto();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [closeLightbox, prevPhoto, nextPhoto]);

  return (
    <div>
      {/* Category filter */}
      <div className="flex items-center gap-2 mb-5">
        {categories.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 ${
              cat === c.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
          >
            {c.label}
          </button>
        ))}
        <span className="ml-auto text-slate-400 text-xs">{filtered.length} photos</span>
      </div>

      {/* Main featured + grid */}
      <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[480px] rounded-2xl overflow-hidden">
        {/* Main big photo */}
        <div
          className="col-span-2 row-span-2 relative group cursor-pointer"
          onClick={() => setLightbox(0)}
        >
          <img src={filtered[0]?.src} alt={filtered[0]?.label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <i className="ri-zoom-in-line text-slate-800"></i>
            </div>
          </div>
          <div className="absolute bottom-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg backdrop-blur-sm">
            {filtered[0]?.label}
          </div>
        </div>

        {/* Secondary photos */}
        {filtered.slice(1, 5).map((photo, i) => (
          <div
            key={i}
            className="relative group cursor-pointer overflow-hidden"
            onClick={() => setLightbox(i + 1)}
          >
            <img src={photo.src} alt={photo.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
            {i === 3 && filtered.length > 5 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-xl">+{filtered.length - 5}</span>
              </div>
            )}
            <div className="absolute bottom-2 left-2 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
              {photo.label}
            </div>
          </div>
        ))}
      </div>

      {/* Voir toutes les photos */}
      <button
        onClick={() => setLightbox(0)}
        className="mt-3 flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
      >
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-image-line"></i></span>
        Voir toutes les photos ({filtered.length})
      </button>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-all"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            className="absolute left-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-all z-10"
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <div
            className="max-w-5xl max-h-[85vh] w-full px-20"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filtered[lightbox]?.src}
              alt={filtered[lightbox]?.label}
              className="w-full h-full object-contain rounded-xl"
              style={{ maxHeight: "75vh" }}
            />
            <div className="text-center mt-4">
              <p className="text-white/80 text-sm">{filtered[lightbox]?.label}</p>
              <p className="text-white/40 text-xs mt-1">{lightbox + 1} / {filtered.length}</p>
            </div>
          </div>

          <button
            className="absolute right-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-all z-10"
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          {/* Thumbnails strip */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 px-4">
            {filtered.map((p, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setLightbox(i); }}
                className={`w-14 h-10 rounded-lg overflow-hidden cursor-pointer transition-all flex-shrink-0 ${
                  i === lightbox ? "ring-2 ring-white opacity-100" : "opacity-40 hover:opacity-70"
                }`}
              >
                <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
